import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { useTheme } from './theme-provider';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function ComparisonCharts({ chartData }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  if (!chartData || !chartData.labels) {
    return null;
  }

  const textColor = isDark ? '#e5e7eb' : '#1f2937';
  const gridColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  const labels = chartData.labels.map(label => 
    label.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  );

  // Calculate similarity between players for each metric
  const categorizeMetrics = () => {
    const similarMetrics = [];
    const differentMetrics = [];
    
    chartData.labels.forEach((label, index) => {
      const player1Value = chartData.player1_data[index];
      const player2Value = chartData.player2_data[index];
      
      // Handle zero values and calculate relative difference
      const maxValue = Math.max(Math.abs(player1Value), Math.abs(player2Value));
      
      if (maxValue === 0) {
        // Both are zero, consider them similar
        similarMetrics.push(index);
        return;
      }
      
      // Calculate percentage difference
      const difference = Math.abs(player1Value - player2Value);
      const percentageDifference = (difference / maxValue) * 100;
      
      // If difference is less than 30%, consider them similar (for line chart)
      // Otherwise, they're different (for bar chart)
      if (percentageDifference <= 30) {
        similarMetrics.push(index);
      } else {
        differentMetrics.push(index);
      }
    });
    
    return { similarMetrics, differentMetrics };
  };

  const { similarMetrics, differentMetrics } = categorizeMetrics();

  // Prepare data for similar metrics (Line Chart)
  const lineChartData = similarMetrics.length > 0 ? {
    labels: similarMetrics.map(idx => labels[idx]),
    datasets: [
      {
        label: chartData.player1_name || 'Player 1',
        data: similarMetrics.map(idx => chartData.player1_data[idx]),
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: 'rgba(59, 130, 246, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
      {
        label: chartData.player2_name || 'Player 2',
        data: similarMetrics.map(idx => chartData.player2_data[idx]),
        borderColor: 'rgba(147, 51, 234, 1)',
        backgroundColor: 'rgba(147, 51, 234, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: 'rgba(147, 51, 234, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
    ],
  } : null;

  // Prepare data for different metrics (Bar Chart)
  const barChartData = differentMetrics.length > 0 ? {
    labels: differentMetrics.map(idx => labels[idx]),
    datasets: [
      {
        label: chartData.player1_name || 'Player 1',
        data: differentMetrics.map(idx => chartData.player1_data[idx]),
        backgroundColor: 'rgba(59, 130, 246, 0.7)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
      },
      {
        label: chartData.player2_name || 'Player 2',
        data: differentMetrics.map(idx => chartData.player2_data[idx]),
        backgroundColor: 'rgba(147, 51, 234, 0.7)',
        borderColor: 'rgba(147, 51, 234, 1)',
        borderWidth: 2,
      },
    ],
  } : null;

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: textColor,
          usePointStyle: true,
          padding: 15,
        },
      },
    },
  };

  const barChartOptions = {
    ...commonOptions,
    indexAxis: 'y',
    plugins: {
      ...commonOptions.plugins,
      title: {
        display: true,
        text: 'Metrics with Significant Differences',
        font: {
          size: 18,
          weight: 'bold',
        },
        color: textColor,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          color: textColor,
        },
        grid: {
          color: gridColor,
        },
      },
      y: {
        ticks: {
          color: textColor,
        },
        grid: {
          color: gridColor,
        },
      },
    },
  };

  const lineChartOptions = {
    ...commonOptions,
    plugins: {
      ...commonOptions.plugins,
      title: {
        display: true,
        text: 'Similar Metrics Comparison',
        font: {
          size: 18,
          weight: 'bold',
        },
        color: textColor,
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColor,
        },
        grid: {
          color: gridColor,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: textColor,
        },
        grid: {
          color: gridColor,
        },
      },
    },
  };

  return (
    <div className="grid gap-8 md:grid-cols-1 lg:grid-cols-2">
      {/* Bar Chart for Different Metrics */}
      {barChartData && (
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Significant Differences</CardTitle>
            <CardDescription>
              Metrics where players show notable differences (Bar Chart)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              <Bar data={barChartData} options={barChartOptions} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Line Chart for Similar Metrics */}
      {lineChartData && (
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Similar Metrics</CardTitle>
            <CardDescription>
              Metrics where players have similar values (Line Chart)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              <Line data={lineChartData} options={lineChartOptions} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fallback: If all metrics are in one category, show both charts with all data */}
      {(!barChartData || !lineChartData) && (
        <>
          {!barChartData && lineChartData && (
            <Card className="shadow-lg lg:col-span-2">
              <CardHeader>
                <CardTitle>All Metrics Comparison</CardTitle>
                <CardDescription>
                  All metrics show similar values between players
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <Line data={lineChartData} options={lineChartOptions} />
                </div>
              </CardContent>
            </Card>
          )}
          
          {barChartData && !lineChartData && (
            <Card className="shadow-lg lg:col-span-2">
              <CardHeader>
                <CardTitle>All Metrics Comparison</CardTitle>
                <CardDescription>
                  All metrics show significant differences between players
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <Bar data={barChartData} options={barChartOptions} />
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export default ComparisonCharts;
